import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppSelector, useAppDispatch } from '@/store';
import { useAccumulatedPaging } from '@/hooks/common/useAccumulatedPaging';
import { useGetMyThreadsQuery, useGetSavedThreadsQuery } from '@/store/api/corkBoard/corkBoardApi';
import { clearDeletedThreads } from '@/store/slices/residents/corkBoardUISlice';
import { CORK_BOARD_PAGINATION } from '@/constants/residents/corkBoardConstants';
import type { GetUserThreadsParams, ThreadsListResponse } from '@/types/residents/corkBoard';

type UserThreadsQueryHook = typeof useGetMyThreadsQuery | typeof useGetSavedThreadsQuery;

type UserThreadsVariant = 'my' | 'saved';

interface UseUserThreadsListOptions {
  queryHook: UserThreadsQueryHook;
  variant: UserThreadsVariant;
}

export const useUserThreadsList = ({ queryHook, variant }: UseUserThreadsListOptions) => {
  const dispatch = useAppDispatch();
  const propertyId = useAppSelector((state) => state.auth.user?.propertyId);
  const deletedThreadIds = useAppSelector((state) => state.corkBoardUI.deletedThreadIds);
  const [page, setPage] = useState<number>(CORK_BOARD_PAGINATION.defaultPage);
  const [refreshKey, setRefreshKey] = useState(0);

  const queryParams = useMemo<GetUserThreadsParams>(() => ({
    propertyId: propertyId ?? 0,
    page,
    pageSize: CORK_BOARD_PAGINATION.defaultPageSize,
  }), [propertyId, page]);

  const shouldSkip = !propertyId;

  const {
    data: threadsData,
    isLoading,
    isFetching,
    refetch,
    isError,
  } = queryHook(queryParams, { skip: shouldSkip });

  const threads = threadsData?.threads ?? [];
  const pagination = threadsData?.pagination;
  const hasMorePages = pagination ? pagination.currentPage < pagination.totalPages - 1 : false;

  const accumulationKey = useMemo(
    () => `${propertyId}_${variant}_threads_${refreshKey}`,
    [propertyId, variant, refreshKey],
  );

  const { accumulatedRows, clearIfPendingReplaceAndNoRows } = useAccumulatedPaging({
    rows: threads,
    accumulationKey,
  });

  useEffect(() => {
    clearIfPendingReplaceAndNoRows(isLoading);
  }, [isLoading, clearIfPendingReplaceAndNoRows]);

  const visibleRows = useMemo(
    () => {
      const base = accumulatedRows.length > 0 ? accumulatedRows : threads;
      return deletedThreadIds.length > 0
        ? base.filter((t) => !deletedThreadIds.includes(String(t.id)))
        : base;
    },
    [accumulatedRows, threads, deletedThreadIds],
  );

  const isInitialLoad = threadsData === undefined && !isError;
  const isRefreshing = isFetching && !isInitialLoad && page === 0;
  const isLoadingMore = isFetching && page > 0;
  const isEmpty = !isLoading && !isFetching && visibleRows.length === 0;

  const handleRefresh = useCallback(() => {
    dispatch(clearDeletedThreads());
    setPage(CORK_BOARD_PAGINATION.defaultPage);
    setRefreshKey((prev) => prev + 1);
  }, [dispatch]);

  const handleEndReached = useCallback(() => {
    if (!isFetching && hasMorePages) {
      setPage((prev) => prev + 1);
    }
  }, [isFetching, hasMorePages]);

  return {
    threads: visibleRows,
    isInitialLoad,
    isRefreshing,
    isLoadingMore,
    isError,
    isEmpty,
    handleRefresh,
    handleEndReached,
    refetch,
  } as const;
};
